import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Home,
  CreditCard,
  TrendingUp,
  PiggyBank,
  Receipt,
  Bell,
  Gift,
  BarChart3,
  Wallet,
  Settings,
  HelpCircle,
  Target,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Send,
  FileText,
  Shield
} from 'lucide-react'

const getMenuItems = (t) => [
  {
    section: t('sidebar:sections.banking'),
    items: [
      {
        id: 'dashboard',
        label: t('sidebar:menu.dashboard'),
        icon: <Home className="w-5 h-5" />
      },
      {
        id: 'accounts',
        label: t('sidebar:menu.accounts'),
        icon: <CreditCard className="w-5 h-5" />
      },
      {
        id: 'transactions',
        label: t('sidebar:menu.transactions'),
        icon: <Receipt className="w-5 h-5" />
      },
      {
        id: 'transfer',
        label: t('sidebar:menu.sendMoney'),
        icon: <Send className="w-5 h-5" />
      },
      {
        id: 'paybills',
        label: t('sidebar:menu.payBills'),
        icon: <FileText className="w-5 h-5" />
      }
    ]
  },
  {
    section: t('sidebar:sections.wealth'),
    items: [
      {
        id: 'investments',
        label: t('sidebar:menu.investments'),
        icon: <TrendingUp className="w-5 h-5" />
      },
      {
        id: 'crypto',
        label: t('sidebar:menu.crypto'),
        icon: <Wallet className="w-5 h-5" />
      },
      {
        id: 'budgets',
        label: t('sidebar:menu.budgets'),
        icon: <Target className="w-5 h-5" />
      },
      {
        id: 'analytics',
        label: t('sidebar:menu.analytics'),
        icon: <BarChart3 className="w-5 h-5" />
      }
    ]
  },
  {
    section: t('sidebar:sections.loansCredit'),
    items: [
      {
        id: 'loans',
        label: t('sidebar:menu.loans'),
        icon: <PiggyBank className="w-5 h-5" />
      }
    ]
  },
  {
    section: t('sidebar:sections.more'),
    items: [
      {
        id: 'rewards',
        label: t('sidebar:menu.rewards'),
        icon: <Gift className="w-5 h-5" />
      },
      {
        id: 'mobile',
        label: t('sidebar:menu.mobileApp'),
        icon: <Smartphone className="w-5 h-5" />
      },
      {
        id: 'notifications',
        label: t('sidebar:menu.notifications'),
        icon: <Bell className="w-5 h-5" />,
        badge: 3
      },
      {
        id: 'help',
        label: t('sidebar:menu.helpCenter'),
        icon: <HelpCircle className="w-5 h-5" />
      },
      {
        id: 'kyc',
        label: 'Verificación KYC',
        icon: <Shield className="w-5 h-5" />
      },
      {
        id: 'settings',
        label: t('sidebar:menu.settings'),
        icon: <Settings className="w-5 h-5" />
      }
    ]
  }
]

export default function Sidebar({ activeView, onViewChange, isCollapsed, onToggleCollapse, isMobileOpen, onMobileClose, kycRequired = false }) {
  const { t } = useTranslation(['sidebar', 'common'])
  const menuItems = getMenuItems(t)
  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onMobileClose}
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{
          width: isCollapsed ? 80 : 280,
        }}
        transition={{ duration: 0.3 }}
        className={`
          fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
          flex flex-col min-h-screen bg-white border-r border-slate-200
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          transition-transform duration-300
        `}
        style={{
          width: isCollapsed ? '80px' : '280px',
          maxWidth: isCollapsed ? '80px' : '280px'
        }}
      >
      {/* Collapse Button - Hidden on mobile */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex absolute -right-3 top-8 z-50 p-2 bg-white border border-slate-200 rounded-full text-slate-600 hover:text-slate-900 hover:shadow-lg transition-all"
      >
        {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>

      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-lg flex items-center justify-center shadow-lg shadow-amber-200/50">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="font-bold text-lg text-slate-900">{t('common:appName')}</div>
                <div className="text-xs text-amber-600 font-medium">{t('common:tagline')}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {menuItems.map((section, sectionIndex) => (
          <div key={section.section} className={sectionIndex > 0 ? 'mt-6' : ''}>
            <AnimatePresence>
              {!isCollapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="px-3 mb-2"
                >
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    {section.section}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="space-y-1">
              {section.items.map((item) => {
                // When KYC is required, only allow access to KYC page
                const isDisabled = kycRequired && item.id !== 'kyc'
                return (
                  <button
                    key={item.id}
                    onClick={() => !isDisabled && onViewChange(item.id)}
                    disabled={isDisabled}
                    className={`w-full p-3 rounded-lg flex items-center gap-3 transition-all ${
                      activeView === item.id
                        ? 'bg-amber-50 text-amber-600 font-medium'
                        : isDisabled
                        ? 'text-slate-300 cursor-not-allowed opacity-50'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                    title={isDisabled ? 'Complete KYC verification first' : ''}
                  >
                    {/* Active indicator */}
                    {activeView === item.id && (
                      <div className="absolute left-0 w-1 h-8 bg-gradient-to-b from-amber-400 to-amber-600 rounded-r-full" />
                    )}

                    <div className={`flex-shrink-0 ${
                      activeView === item.id ? 'text-amber-600' : isDisabled ? 'text-slate-300' : 'text-slate-500'
                    }`}>
                      {item.icon}
                    </div>

                    <AnimatePresence>
                      {!isCollapsed && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex-1 flex items-center justify-between"
                        >
                          <span className="text-sm">{item.label}</span>
                          {item.badge && !isDisabled && (
                            <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                              {item.badge}
                            </span>
                          )}
                          {isDisabled && (
                            <span className="bg-yellow-100 text-yellow-700 text-xs font-medium px-1.5 py-0.5 rounded">
                              KYC
                            </span>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-200">
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="text-center text-xs text-slate-500"
            >
              <div>{t('common:appName')} v1.0</div>
              <div className="mt-1">© 2024 {t('common:copyright')}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
    </>
  )
}
